import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  IconButton,
  Input,
  InputAdornment,
  Tooltip,
  Dialog,
  DialogActions,
  DialogTitle,
  Grid,
  Typography,
  Chip,
  Avatar,
  ToggleButton,
  ToggleButtonGroup,
  Divider,
  Stack,
  Paper,
  Container,
  Fade,
  Zoom,
  FormControl,
  Select,
  MenuItem
} from "@mui/material";
import React, { useEffect, useState, useCallback } from "react";
import { debounce } from "lodash";
import CloseIcon from "@mui/icons-material/Close";
import VisibilityIcon from "@mui/icons-material/Visibility";
import ViewListIcon from "@mui/icons-material/ViewList";
import ViewModuleIcon from "@mui/icons-material/ViewModule";
import PeopleIcon from "@mui/icons-material/People";
import DataTable from "react-data-table-component";
import { userServices } from "../../services/AllServices";
import errorHandler from "../../utils/errorHandler";
import { Loader } from "../../components/CustomLoader";
import { Helmet } from "react-helmet-async";
import { TrimText, capitalize } from "../../utils/helpers";
import { fDate } from "../../utils/formatTime";
import ViewFriendManagement from "./ViewFriend";
import { useNavigate, useSearchParams } from "react-router-dom";

export default function FriendManagement() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchItem, setSearchItem] = useState("");
  const [data, setData] = useState([]);
  const [totalPages, setTotalPages] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [show, setShow] = useState(false);
  const [warning, setWarning] = useState("");
  const [refresh, setRefresh] = useState(false);
  const [modalShow, setModalShow] = useState(false);
  const [viewId, setViewId] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [loaderDelay, setLoaderDelay] = useState(false);
  const [viewMode, setViewMode] = useState('grid'); // 'grid' or 'list'
  const [totalFriends, setTotalFriends] = useState(0);
  const [totalActiveFriends, setTotalActiveFriends] = useState(0);
  const [totalInactiveFriends, setTotalInactiveFriends] = useState(0);

  const fetchUsers = async (query) => {
    const body = {
      searchItem: query || "",
      pageNumber: currentPage,
      pageSize: pageSize,
    };
    try {
      setIsLoading(true);
      setLoaderDelay(false);
      const res = await userServices.getUser(body);
      setTimeout(() => {
        if (res.status === 200) {
          setData(res?.data?.data?.result);
          setTotalPages(res?.data?.data?.totalRecords);
          
          // Calculate stats
          if (res?.data?.data?.result) {
            const friends = res.data.data.result;
            setTotalFriends(friends.length);
            setTotalActiveFriends(friends.filter(item => item.status === 1).length);
            setTotalInactiveFriends(friends.filter(item => item.status === 0).length);
          }
        }
        setLoaderDelay(true);
      }, 500);
    } catch (err) {
      setData(err?.response?.data?.message?.data);
      errorHandler(err);
      setLoaderDelay(false);
    } finally {
      setIsLoading(false);
      setLoaderDelay(false);
    }
  };

  const debouncedSearch = useCallback(
    debounce((query) => fetchUsers(query), 400),
    [currentPage, pageSize]
  );

  const handleSearchChange = (event) => {
    const query = event.target.value;
    setSearchItem(query);
    setCurrentPage(1);
    debouncedSearch(query);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
    // Update URL parameter to maintain page state
    const newSearchParams = new URLSearchParams(searchParams);
    newSearchParams.set('page', page);
    setSearchParams(newSearchParams);
  };

  // Initialize currentPage, viewMode, and pageSize from URL parameters
  useEffect(() => {
    const pageFromUrl = searchParams.get('page');
    const viewModeFromUrl = searchParams.get('view');
    const pageSizeFromUrl = searchParams.get('pageSize');
    
    if (pageFromUrl && !isNaN(pageFromUrl) && parseInt(pageFromUrl) > 0) {
      setCurrentPage(parseInt(pageFromUrl));
    }
    
    if (viewModeFromUrl && (viewModeFromUrl === 'grid' || viewModeFromUrl === 'list')) {
      setViewMode(viewModeFromUrl);
    }
    
    if (pageSizeFromUrl && !isNaN(pageSizeFromUrl) && parseInt(pageSizeFromUrl) > 0) {
      setPageSize(parseInt(pageSizeFromUrl));
    }
  }, [searchParams]);

  useEffect(() => {
    fetchUsers(searchItem);
  }, [currentPage, pageSize, refresh]);

  const columns = [
    {
      name: "S.No.",
      cell: (row, index) => (currentPage - 1) * pageSize + index + 1,
      width: "100px",
    },
    {
      name: "User Name",
      sortable: true,
      selector: (row) => TrimText(row.username, 25),
    },
    {
      name: "Email",
      sortable: true,
      selector: (row) => row.email || 'N/A',
    },
    {
      name: "Phone",
      sortable: true,
      selector: (row) => row.phoneNumber || 'N/A',
    },
    {
      name: "Action",
      right: true,
      cell: (row) => (
        <>
           <Tooltip title="View" placement="top">
            <IconButton
              color="primary"
              onClick={() => {
                navigate(`/friend-management/view/${row?._id}?page=${currentPage}&view=${viewMode}`);
              }}
            >
              <VisibilityIcon />
            </IconButton>
          </Tooltip>
        </>
      ),
    },
  ];

  const renderFriendCard = (friend, index) => (
    <Zoom in={!isLoading} className={`friend-card-zoom friend-card-zoom-${index}`}>
      <Grid item xs={12} sm={6} md={4} lg={3} key={friend._id}>
        <Paper
          elevation={0}
          sx={{
            p: 3,
            borderRadius: 3,
            background: 'linear-gradient(145deg, rgba(255, 255, 255, 0.03) 0%, rgba(255, 255, 255, 0.05) 100%)',
            border: '1px solid',
            borderColor: 'divider',
            transition: 'all 0.3s ease',
            cursor: 'pointer',
            '&:hover': {
              borderColor: 'primary.main',
              transform: 'translateY(-4px)',
              boxShadow: '0 12px 40px rgba(0, 161, 221, 0.2)',
            }
          }}
        >
          {/* Header */}
          <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
            <Avatar
              sx={{
                width: 48,
                height: 48,
                background: 'linear-gradient(135deg, #00a1dd 0%, #42a5f5 100%)',
                boxShadow: '0 4px 16px rgba(0, 161, 221, 0.3)'
              }}
            >
              <PeopleIcon sx={{ fontSize: 24, color: 'white' }} />
            </Avatar>
            <Chip
              label={friend.status === 1 ? "Active" : "Inactive"}
              size="small"
              sx={{
                backgroundColor: friend.status === 1 
                  ? 'rgba(76, 175, 80, 0.2)' 
                  : 'rgba(244, 67, 54, 0.2)',
                color: friend.status === 1 ? '#4caf50' : '#f44336',
                fontWeight: 600,
                border: '1px solid',
                borderColor: friend.status === 1 
                  ? 'rgba(76, 175, 80, 0.3)' 
                  : 'rgba(244, 67, 54, 0.3)'
              }}
            />
          </Stack>

          {/* Content */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="h6" sx={{ color: 'white', fontWeight: 700, mb: 1 }}>
              {TrimText(friend.username, 30)}
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1 }}>
              {friend.email || 'N/A'}
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
              {friend.phoneNumber || 'N/A'}
            </Typography>
            <Typography variant="caption" sx={{ color: 'text.disabled', display: 'block' }}>
              Joined: {fDate(friend.createdAt)}
            </Typography>
          </Box>

          {/* Actions */}
          <Stack direction="row" spacing={1}>
            <Button
              size="small"
              variant="contained"
              startIcon={<VisibilityIcon />}
              onClick={() => navigate(`/friend-management/view/${friend._id}?page=${currentPage}&view=${viewMode}`)}
              sx={{
                borderRadius: 2,
                background: 'linear-gradient(135deg, #00a1dd 0%, #42a5f5 100%)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #0091c8 0%, #3b8fd9 100%)',
                }
              }}
            >
              View
            </Button>
          </Stack>
        </Paper>
      </Grid>
    </Zoom>
  );

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      <Helmet>
        <title>Friend Management</title>
      </Helmet>
      <Fade in timeout={500}>
        <Stack spacing={4}>
          {/* Header Section */}
          <Paper
            elevation={0}
            sx={{
              p: 4,
              borderRadius: 3,
              background: 'linear-gradient(135deg, #1a202c 0%, #2d3748 100%)',
              border: '1px solid',
              borderColor: 'divider',
              position: 'relative',
              overflow: 'hidden',
              '&::before': {
                content: '""',
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                height: '4px',
                background: 'linear-gradient(90deg, #00a1dd 0%, #42a5f5 50%, #90caf9 100%)',
              }
            }}
          >
            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 3 }}>
              <Stack direction="row" alignItems="center" spacing={3}>
                <Avatar
                  sx={{
                    width: 64,
                    height: 64,
                    background: 'linear-gradient(135deg, #00a1dd 0%, #42a5f5 100%)',
                    boxShadow: '0 8px 32px rgba(0, 161, 221, 0.4)'
                  }}
                >
                  <PeopleIcon sx={{ fontSize: 32, color: 'white' }} />
                </Avatar>
                <Box>
                  <Typography variant="h4" sx={{ color: 'white', fontWeight: 700, mb: 1 }}>
                    Friend Management
                  </Typography>
                  <Typography variant="body1" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                    Manage and monitor all friends in your system
                  </Typography>
                </Box>
              </Stack>
            </Stack>

            {/* Stats Cards */}
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6} md={3}>
                <Paper
                  sx={{
                    p: 3,
                    borderRadius: 2,
                    background: 'rgba(0, 161, 221, 0.1)',
                    border: '1px solid rgba(0, 161, 221, 0.2)',
                    textAlign: 'center'
                  }}
                >
                  <Typography variant="h4" sx={{ color: 'primary.main', fontWeight: 700, mb: 1 }}>
                    {data?.length || 0}
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.8)' }}>
                    Total Friends
                  </Typography>
                </Paper>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Paper
                  sx={{
                    p: 3,
                    borderRadius: 2,
                    background: 'rgba(76, 175, 80, 0.1)',
                    border: '1px solid rgba(76, 175, 80, 0.2)',
                    textAlign: 'center'
                  }}
                >
                  <Typography variant="h4" sx={{ color: '#4caf50', fontWeight: 700, mb: 1 }}>
                    {totalActiveFriends}
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.8)' }}>
                    Active Friends
                  </Typography>
                </Paper>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Paper
                  sx={{
                    p: 3,
                    borderRadius: 2,
                    background: 'rgba(244, 67, 54, 0.1)',
                    border: '1px solid rgba(244, 67, 54, 0.2)',
                    textAlign: 'center'
                  }}
                >
                  <Typography variant="h4" sx={{ color: '#f44336', fontWeight: 700, mb: 1 }}>
                    {totalInactiveFriends}
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.8)' }}>
                    Inactive Friends
                  </Typography>
                </Paper>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Paper
                  sx={{
                    p: 3,
                    borderRadius: 2,
                    background: 'rgba(156, 39, 176, 0.1)',
                    border: '1px solid rgba(156, 39, 176, 0.2)',
                    textAlign: 'center'
                  }}
                >
                  <Typography variant="h4" sx={{ color: '#9c27b0', fontWeight: 700, mb: 1 }}>
                    {totalPages}
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.8)' }}>
                    Total Records
                  </Typography>
                </Paper>
              </Grid>
            </Grid>
          </Paper>

          {/* Controls Section */}
          <Paper
            elevation={0}
        sx={{
              p: 3,
              borderRadius: 3,
              background: 'rgba(255, 255, 255, 0.02)',
              border: '1px solid',
              borderColor: 'divider'
            }}
          >
            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ flexWrap: 'wrap', gap: 2 }}>
              <Stack direction="row" alignItems="center" spacing={2} sx={{ flexWrap: 'wrap', gap: 2 }}>
        <Input
          value={searchItem}
                  placeholder="Search friends..."
          onChange={handleSearchChange}
                  sx={{
                    color: 'white',
                    '& .MuiInput-input': {
                      color: 'white',
                    },
                    '& .MuiInput-input::placeholder': {
                      color: 'rgba(255, 255, 255, 0.5)',
                      opacity: 1,
                    },
                    '& .MuiInput-root': {
                      backgroundColor: 'rgba(255, 255, 255, 0.05)',
                      borderRadius: 2,
                      padding: '8px 16px',
                    }
                  }}
          endAdornment={
            <InputAdornment position="end">
              <IconButton
                aria-label="clear-search"
                onClick={() => {
                  const cleared = "";
                  setSearchItem(cleared);
                  debouncedSearch(cleared);
                }}
                        sx={{ color: 'text.secondary' }}
              >
                <CloseIcon />
              </IconButton>
            </InputAdornment>
          }
        />
              </Stack>

              <ToggleButtonGroup
                value={viewMode}
                exclusive
                onChange={(e, newMode) => {
                  if (newMode) {
                    setViewMode(newMode);
                    // Update URL parameter to maintain view mode
                    const newSearchParams = new URLSearchParams(searchParams);
                    newSearchParams.set('view', newMode);
                    setSearchParams(newSearchParams);
                  }
                }}
                sx={{
                  '& .MuiToggleButton-root': {
                    color: 'text.secondary',
                    borderColor: 'divider',
                    '&.Mui-selected': {
                      backgroundColor: 'primary.main',
                      color: 'white',
                      '&:hover': {
                        backgroundColor: 'primary.dark',
                      }
                    }
                  }
                }}
              >
                <ToggleButton value="grid">
                  <ViewModuleIcon />
                </ToggleButton>
                <ToggleButton value="list">
                  <ViewListIcon />
                </ToggleButton>
              </ToggleButtonGroup>
            </Stack>
          </Paper>

          {/* Content Section */}
          <Paper
            elevation={0}
            sx={{
              borderRadius: 3,
              background: 'rgba(255, 255, 255, 0.02)',
              border: '1px solid',
              borderColor: 'divider',
              overflow: 'hidden'
            }}
          >
            {viewMode === 'grid' ? (
              /* Grid View */
              <Box sx={{ p: 3 }}>
                {isLoading || !loaderDelay ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                    <Loader />
                  </Box>
                ) : !data || data.length === 0 ? (
                  <Box sx={{ textAlign: 'center', py: 8 }}>
                    <Avatar
                      sx={{
                        width: 80,
                        height: 80,
                        mx: 'auto',
                        mb: 3,
                        background: 'linear-gradient(135deg, #9c27b0 0%, #e91e63 100%)',
                        boxShadow: '0 8px 32px rgba(156, 39, 176, 0.3)'
                      }}
                    >
                      <PeopleIcon sx={{ fontSize: 40 }} />
                    </Avatar>
                    <Typography variant="h6" color="text.secondary" sx={{ mb: 2, fontWeight: 600 }}>
                      No friends found
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Try adjusting your search criteria
                    </Typography>
                  </Box>
                ) : (
                  <>
                    <Grid container spacing={3}>
                      {data.map((friend, index) => renderFriendCard(friend, index))}
                    </Grid>
                    
                    {/* Pagination Controls (Grid View) */}
                    <Box sx={{ 
                      display: 'flex', 
                      justifyContent: 'space-between', 
                      alignItems: 'center', 
                      gap: 2,
                      flexWrap: 'wrap',
                      mt: 4,
                      p: 2,
                      borderRadius: 2,
                      background: 'rgba(255, 255, 255, 0.02)',
                      border: '1px solid',
                      borderColor: 'divider'
                    }}>
                      {/* Left side - Page Size Selector */}
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="body2" sx={{ color: 'white', whiteSpace: 'nowrap' }}>
                          Items per page:
                        </Typography>
                        <FormControl size="small">
                          <Select
                            value={pageSize}
                            onChange={(e) => {
                              setPageSize(e.target.value);
                              setCurrentPage(1);
                              const newSearchParams = new URLSearchParams(searchParams);
                              newSearchParams.set('pageSize', e.target.value);
                              newSearchParams.set('page', '1');
                              setSearchParams(newSearchParams);
                            }}
                            displayEmpty
                            sx={{
                              color: 'white',
                              minWidth: 80,
                              '& .MuiOutlinedInput-notchedOutline': {
                                borderColor: 'rgba(255, 255, 255, 0.3)',
                              },
                              '&:hover .MuiOutlinedInput-notchedOutline': {
                                borderColor: 'rgba(255, 255, 255, 0.5)',
                              },
                              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                borderColor: 'rgba(255, 255, 255, 0.7)',
                              },
                              '& .MuiSvgIcon-root': {
                                color: 'white',
                              },
                            }}
                          >
                            <MenuItem value={2}>2</MenuItem>
                            <MenuItem value={5}>5</MenuItem>
                            <MenuItem value={10}>10</MenuItem>
                            <MenuItem value={20}>20</MenuItem>
                            <MenuItem value={50}>50</MenuItem>
                          </Select>
                        </FormControl>
                      </Box>

                      {/* Right side - Navigation Controls */}
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Button
                          variant="outlined"
                          size="small"
                          disabled={currentPage === 1}
                          onClick={() => handlePageChange(currentPage - 1)}
                          sx={{ color: 'white', borderColor: 'rgba(255, 255, 255, 0.3)' }}
                        >
                          Previous
                        </Button>
                        <Typography variant="body2" sx={{ color: 'white', mx: 1 }}>
                          Page {currentPage} of {Math.ceil(totalPages / pageSize)}
                        </Typography>
                        <Button
                          variant="outlined"
                          size="small"
                          disabled={currentPage >= Math.ceil(totalPages / pageSize)}
                          onClick={() => handlePageChange(currentPage + 1)}
                          sx={{ color: 'white', borderColor: 'rgba(255, 255, 255, 0.3)' }}
                        >
                          Next
                        </Button>
                      </Box>
                    </Box>
                  </>
                )}
      </Box>
            ) : (
              /* List View */
              <Box>
        <DataTable
          customStyles={{
            headCells: {
              style: {
                fontSize: "15px",
                fontWeight: "bold",
                        backgroundColor: 'rgba(255, 255, 255, 0.02)',
                        borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
                        color: 'black'
                      },
                    },
                    rows: {
                      style: {
                        backgroundColor: 'transparent',
                        color: 'white',
                        '&:hover': {
                          backgroundColor: 'rgba(255, 255, 255, 0.02)',
                        }
                      }
                    },
                    cells: {
                      style: {
                        color: 'white',
                      }
                    }
          }}
          columns={columns}
          data={data}
          pagination
          paginationServer
          paginationTotalRows={totalPages}
                  paginationCurrentPage={currentPage}
                  paginationDefaultPage={currentPage}
          onChangePage={handlePageChange}
          paginationPerPage={pageSize}
          paginationRowsPerPageOptions={[10, 20, 50]}
          onChangeRowsPerPage={(currentRowsPerPage) =>
            setPageSize(currentRowsPerPage)
          }
          progressPending={isLoading || !loaderDelay}
          progressComponent={<Loader />}
          className="custom"
        />
              </Box>
            )}
          </Paper>
        </Stack>
      </Fade>

      {/* Dialog */}
      <Dialog
        open={show}
        onClose={() => setShow(false)}
        className="popupbox-statis"
      >
        <DialogTitle>{warning}</DialogTitle>
        <DialogActions className="status-box-action">
          <Button
            variant="outlined"
            onClick={() => setShow(false)}
            color="primary"
          >
            No
          </Button>
        </DialogActions>
      </Dialog>
      {modalShow ? (
        <ViewFriendManagement
          show={modalShow}
          onHide={() => setModalShow(false)}
          id={viewId}
        />
      ) : (
        ""
      )}
    </Container>
  );
}
